import React from "react";
import { Box, Button } from "@mui/material";

import Panel from "../../../components/Panel";
import IpdAdmissionForm from "./IpdAdmissionForm";
import { t } from "../../../components/translate";
import useServiceProviders from "../../../hooks/useServiceProviders";
import { usePostIpd, usePatchIpd } from "../../../hooks/query/useIpds";
import StatefulButton from "../../../components/StatefulButton/StatefulButton";

import { EmergencyContact, IPD, TIpdCase } from "../../../interfaces/IpdInterface";
import { Client } from "../../../interfaces/ClientInterface";
import { IpdCases } from "../../../constants/ipd";
import { useWardTypeData } from "../../../hooks/useWardTypeData";
import { useWardTypeFromUrl } from "../../../hooks/useWardTypeFromUrl";

export interface IPDFormState {
  // basic data
  id: number | null;
  ipdNumber: number | null;
  referenceNumber: string | null;

  // clinical data
  hopi: string | null;
  admissionCase: TIpdCase | null;
  currentCase: TIpdCase | null;
  admissionCondition: string | null;
  provisionalDiagnosis: string | null;
  triagePriority: string | null;

  // admission locations
  wardId: number | null;
  roomId: number | null;
  bedId: number | null;

  claimCode: string;
  expectedLengthOfStay: string | null;
  emergencyContact: EmergencyContact;

  // using ids so no need for these numbers
  // bedNumber: number | null;
  // wardNumber: number | null;

  // room has type so this is not needed
  // roomType: string;

  client: Client | null;
  referredBy: string | null;
  doctorInChargeId: number | null;
  authorizedPersonnelId: number | null;
}

const initialIpdState: IPDFormState = {
  id: null,
  ipdNumber: null,
  referenceNumber: "",

  wardId: null,
  roomId: null,
  bedId: null,
  expectedLengthOfStay: null,

  hopi: "",
  triagePriority: null,
  admissionCondition: "",
  provisionalDiagnosis: "",
  admissionCase: IpdCases.GENERAL,
  currentCase: IpdCases.GENERAL,

  claimCode: "",
  emergencyContact: {
    name: "",
    phone: "",
    relation: ""
  },

  client: {} as Client,
  referredBy: "",
  doctorInChargeId: null,
  authorizedPersonnelId: null
};

interface Props {
  ipd?: IPD | null;
  openedFromBed?: boolean;
  defaultIpdCase?: TIpdCase;
  initialBedAssignment?: {
    wardId: number;
    roomId: number;
    bedId: number;
  } | null;
  onClose: () => void;
  onCreateSuccess?: (createdIpd: IPD) => void;
  onEditSuccess?: (createdIpd: IPD) => void;
}

export const IpdAdmissionCreateEdit = ({
  ipd,
  openedFromBed = false,
  defaultIpdCase,
  initialBedAssignment = null,
  onClose,
  onCreateSuccess,
  onEditSuccess
}: Props): React.ReactNode => {
  const isEdit = !!ipd?.id;

  // custom hooks
  const wardType = useWardTypeFromUrl();
  const serviceProviders = useServiceProviders({ active: true });

  // query hooks
  const { mutate: createIpd, isLoading: isCreating } = usePostIpd();
  const { mutate: updateIpd, isLoading: isUpdating } = usePatchIpd();
  const { extendedBeds, rooms, wards } = useWardTypeData({ wardType });

  // local states
  const [ipdFormData, setIpdFormData] = React.useState<IPDFormState>({
    ...initialIpdState,
    admissionCase: defaultIpdCase ?? IpdCases.GENERAL,
    currentCase: defaultIpdCase ?? IpdCases.GENERAL
  });

  const isValid = (data: IPDFormState) => {
    if (
      data.admissionCase &&
      data.currentCase &&
      data.client?.id &&
      data.emergencyContact.name &&
      data.emergencyContact.phone &&
      data.doctorInChargeId &&
      data.authorizedPersonnelId
    ) {
      return false;
    }

    return true;
  };

  const disable = isValid(ipdFormData);

  const handleSave = () => {
    if (isEdit) {
      updateIpd(
        { id: ipdFormData.id as number, data: ipdFormData },
        {
          onSuccess: (res) => {
            onEditSuccess?.(res.data as IPD);
          }
        }
      );
    } else {
      createIpd(ipdFormData, {
        onSuccess: (res) => {
          onCreateSuccess?.(res.data as IPD);
        }
      });
    }
  };

  React.useEffect(() => {
    if (isEdit) {
      setIpdFormData({
        id: ipd.id,
        ipdNumber: ipd.ipdNumber,
        referenceNumber: ipd.referenceNumber,

        hopi: ipd.ipdRecord.hopi,
        admissionCase: ipd?.admissionCase || IpdCases.GENERAL,
        currentCase: ipd?.currentCase || ipd?.ipdRecord?.case || IpdCases.GENERAL,
        triagePriority: ipd.triagePriority ?? null,
        admissionCondition: ipd.ipdRecord.admissionCondition,
        expectedLengthOfStay: ipd.expectedLengthOfStay || null,
        provisionalDiagnosis: ipd.ipdRecord.provisionalDiagnosis || "",

        bedId: null,
        wardId: null,
        roomId: null,
        emergencyContact: ipd.emergencyContact || null,

        claimCode: ipd.claimCode || "",
        client: ipd?.client || null,
        doctorInChargeId: ipd.doctorInChargeId || null,
        authorizedPersonnelId: ipd.authorizedPersonnelId || null,
        referredBy: ipd.ipdRecord.referredBy
      });
    }
  }, [ipd, isEdit]);

  React.useEffect(() => {
    if (initialBedAssignment) {
      setIpdFormData((prev) => ({
        ...prev,
        bedId: initialBedAssignment.bedId,
        roomId: initialBedAssignment.roomId,
        wardId: initialBedAssignment.wardId
      }));
    }
  }, [initialBedAssignment, openedFromBed]);

  return (
    <Panel
      onClose={onClose}
      title={isEdit ? "Update IPD Form" : t("CreateIPDForm")}
      footer={
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} width="100%">
          <Button
            data-testmation="ipd.cancel"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>

          <StatefulButton
            data-testmation="ipdSave"
            variant="contained"
            color="primary"
            disabled={isCreating || isUpdating || disable}
            onClick={handleSave}
            isLoading={isCreating || isUpdating}
            circularProgressProps={{ size: 16 }}
          >
            Save
          </StatefulButton>
        </Box>
      }
    >
      <Box width="100%">
        <IpdAdmissionForm
          ipdFormData={ipdFormData}
          setIpdFormData={setIpdFormData}
          serviceProviders={serviceProviders}
          mode={isEdit ? "edit" : "create"}
          openedFromBed={openedFromBed}
          wardOptions={wards}
          roomOptions={rooms}
          bedOptions={extendedBeds}
        />
      </Box>
    </Panel>
  );
};
